import {
  Accordion,
  ActionIcon,
  Badge,
  Box,
  Group,
  Stack,
  Switch,
  Text as MantineText,
  ThemeIcon,
  Tooltip,
} from '@mantine/core';
import { IconCheck, IconPlug, IconTrash, IconX } from '@tabler/icons';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useState } from 'react';
import SelectFieldList from '../../../Organismns/Settings/SelectFieldList';
import { ConfirmDialog, useModalState } from '../../../Molecules/Dialogs';
import { ToastS } from '../../../Service/ToastS';
import { deleteWebForm, patchWebFormActive } from '../../../Service/restapi/webformService';

const readableBadgeStyles = (theme, color) => ({
  root: {
    backgroundColor: theme.colorScheme === 'dark' ? color.darkBackground : color.background,
    color: theme.colorScheme === 'dark' ? color.darkText : color.text,
    textTransform: 'none',
    fontWeight: 700,
  },
  leftSection: {
    display: 'flex',
    alignItems: 'center',
    marginRight: 4,
    color: 'currentColor',
  },
});

const badgeColors = {
  active: {
    background: '#DFF7E7',
    text: '#0B5D2A',
    darkBackground: 'rgba(64, 192, 87, 0.22)',
    darkText: '#B2F2BB',
  },
  inactive: {
    background: '#EEF1F4',
    text: '#343A40',
    darkBackground: 'rgba(134, 142, 150, 0.24)',
    darkText: '#F1F3F5',
  },
  details: {
    background: '#E7F0FF',
    text: '#17458F',
    darkBackground: 'rgba(51, 154, 240, 0.22)',
    darkText: '#D0EBFF',
  },
};

const connectorIconStyles = (theme) => ({
  backgroundColor: theme.colorScheme === 'dark' ? 'rgba(253, 126, 20, 0.24)' : '#FFE8CC',
  color: theme.colorScheme === 'dark' ? '#FFD8A8' : '#9C4D00',
  flexShrink: 0,
});

const getFormLocation = (form) => {
  const hostname = form.hostname ?? '';
  const path = form.path ?? '';

  if (!hostname && !path) {
    return 'Unbekannte Webseite';
  }

  return `${hostname}${path}`;
};

const WebForm = ({ form: initialForm, reloadCallback }) => {
  const { show: deleteModalOpen, toggle: toggleDeleteModal } = useModalState();
  const [form, setForm] = useState(initialForm);
  const [activeLoading, setActiveLoading] = useState(false);
  const formLocation = getFormLocation(form);

  useEffect(() => {
    setForm(initialForm);
  }, [initialForm]);

  const deleteForm = useCallback(async () => {
    try {
      const resp = await deleteWebForm(form.id.toString());
      if (!resp.ok) {
        ToastS.generalError();
        return;
      }
      reloadCallback();
    } catch (_error) {
      ToastS.generalError();
    }
  }, [form.id, reloadCallback]);

  const updateActive = useCallback(
    async (checked) => {
      setActiveLoading(true);

      try {
        const resp = await patchWebFormActive(form.id.toString(), checked);
        if (!resp.ok) {
          ToastS.generalError();
          return;
        }

        const updatedForm = await resp.json();
        setForm(updatedForm);
      } catch (_error) {
        ToastS.generalError();
      } finally {
        setActiveLoading(false);
      }
    },
    [form.id],
  );

  return (
    <Accordion.Item value={form.id.toString()}>
      <Box
        sx={(theme) => ({
          display: 'flex',
          alignItems: 'center',
          paddingRight: theme.spacing.sm,
          [theme.fn.smallerThan('sm')]: {
            alignItems: 'stretch',
            flexDirection: 'column',
            paddingRight: 0,
          },
        })}
      >
        <Accordion.Control sx={{ flex: 1, minWidth: 0 }}>
          <Group noWrap spacing="sm" align="flex-start">
            <ThemeIcon radius="xl" size="lg" sx={connectorIconStyles}>
              <IconPlug size={18} stroke={2.5} />
            </ThemeIcon>
            <Stack spacing={2} sx={{ minWidth: 0 }}>
              <Group spacing={6} align="center">
                <MantineText weight={700}>Verbundenes Formular</MantineText>
                <Badge
                  size="sm"
                  styles={(theme) =>
                    readableBadgeStyles(theme, form.active ? badgeColors.active : badgeColors.inactive)
                  }
                  leftSection={form.active ? <IconCheck size={12} stroke={3} /> : <IconX size={12} stroke={3} />}
                >
                  {form.active ? 'Aktiv' : 'Inaktiv'}
                </Badge>
              </Group>
              <MantineText size="xs" color="dimmed" className="overflow-ellipsis">
                {formLocation}
              </MantineText>
            </Stack>
          </Group>
        </Accordion.Control>

        <Group
          noWrap
          spacing="xs"
          sx={(theme) => ({
            flexShrink: 0,
            [theme.fn.smallerThan('sm')]: {
              justifyContent: 'space-between',
              padding: `0 ${theme.spacing.md}px ${theme.spacing.sm}px ${theme.spacing.md}px`,
            },
          })}
        >
          <Box onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
            <Switch
              size="sm"
              label="Aktiv"
              labelPosition="left"
              checked={!!form.active}
              disabled={activeLoading}
              onChange={(event) => updateActive(event.currentTarget.checked)}
            />
          </Box>
          <Tooltip label="Formular-Verknüpfung entfernen" withArrow>
            <ActionIcon
              color="red"
              variant="subtle"
              aria-label="Formular-Verknüpfung entfernen"
              onClick={(event) => {
                event.stopPropagation();
                toggleDeleteModal();
              }}
            >
              <IconTrash size={18} />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Box>

      <Accordion.Panel>
        <SelectFieldList webForm={form} />
      </Accordion.Panel>

      <ConfirmDialog
        text="webForm.delete.confirm"
        subject="webForm.delete.confirm.head"
        open={deleteModalOpen}
        toggle={toggleDeleteModal}
        handler={deleteForm}
      />
    </Accordion.Item>
  );
};

WebForm.propTypes = {
  form: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    active: PropTypes.bool,
    hostname: PropTypes.string,
    path: PropTypes.string,
  }).isRequired,
  reloadCallback: PropTypes.func.isRequired,
};

export default WebForm;
